import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Globe, Check, AlertCircle, Moon, Sun, Settings, X } from 'lucide-react';
import { useUI } from '../contexts/UIContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n';
import withTranslation from '../hoc/withTranslation';
import LetterGlitch from '../components/LetterGlitch';

// Render delay constant
const RENDER_DELAY = 50;

/**
 * Geliştirilmiş Dil ve Bölge Ayarları Ekranı
 * 
 * Modern ve görsel olarak geliştirilmiş, DAPPS tasarım dilini kullanan
 * ekran tasarımı. Kullanıcının uygulama dilini ve bölge ayarlarını değiştirdiği ekran.
 * 
 * @param {Object} props - Bileşen özellikleri
 * @param {Function} props.t - Çeviri fonksiyonu (withTranslation HOC tarafından sağlanır)
 */
const LanguageAndRegionScreen = ({ t }) => {
  const { goBack } = useUI();
  const { currentLanguage, languages, changeLanguage, isChangingLanguage } = useLanguage();

  // State hooks
  const [changeError, setChangeError] = useState(null);
  const [changeSuccess, setChangeSuccess] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [showPreferences, setShowPreferences] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => {
    return localStorage.getItem('reducedMotion') === 'true';
  });
  
  // Optimized rendering control
  const [isInitializing, setIsInitializing] = useState(true);
  const [renderPhase, setRenderPhase] = useState('initial');
  const [areComponentsReady, setAreComponentsReady] = useState(false);
  
  // References
  const containerRef = useRef(null);
  const themeIconRef = useRef(null);
  const backgroundRef = useRef(null);
  
  // Different character sets for light and dark modes
  const charSets = {
    // Dark mode - typical hacker style with many symbols
    dark: [
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
      'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
      '!', '@', '#', '$', '&', '*', '(', ')', '-', '_', '+', '=', '/',
      '[', ']', '{', '}', ';', ':', '<', '>', ',', '0', '1', '2', '3',
      '4', '5', '6', '7', '8', '9', '|', '\\', '~', '^', '%'
    ],
    // Light mode - security and identity focused characters
    light: [
      // More frequent use of letters
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
      'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
      // Security symbols
      '✓', '✔', '⚷', '⚿', '⚹', '⛨', '⛓',
      // ID/Document symbols
      '◫', '◰', '◲', '◳', '◫', '◻', '◼', '◧', '◨',
      // Numbers for ID verification
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'
    ]
  };

  // Glitch themes for the background
  const glitchThemes = {
    dark: {
      colors: ['#1e40af', '#3b82f6', '#60a5fa'], // Blue theme for dark mode
      textGlowColor: 'rgba(96, 165, 250, 0.8)',
      overlayOpacity: 0.7,
      bgColor: '#0f172a', // Dark blue background color
      charSet: charSets.dark,
      glitchSpeed: reducedMotion ? 300 : 50,
      centerVignette: true,
      matrixIntensity: reducedMotion ? 0.4 : 0.8,
      verticalFade: 0.2,
      density: reducedMotion ? 0.1 : 0.25,
    },
    light: {
      colors: ['#312e81', '#4f46e5', '#818cf8'], // Indigo theme for light mode
      textGlowColor: 'rgba(79, 70, 229, 0.8)',
      overlayOpacity: 0.55,
      bgColor: '#f8fafc', // Light background color
      charSet: charSets.light,
      glitchSpeed: reducedMotion ? 400 : 150,
      centerVignette: false,
      matrixIntensity: reducedMotion ? 0.3 : 0.6,
      verticalFade: 0.15,
      density: reducedMotion ? 0.08 : 0.25,
    }
  };

  // Current theme
  const currentTheme = isDarkMode ? glitchThemes.dark : glitchThemes.light;
  
  // Phased rendering to prevent stuttering during initialization
  useEffect(() => {
    if (renderPhase === 'initial') {
      // Phase 1: Set up essential styles and base container
      setTimeout(() => setRenderPhase('background'), RENDER_DELAY);
    } else if (renderPhase === 'background') {
      // Phase 2: Render background elements
      setTimeout(() => setRenderPhase('content'), RENDER_DELAY * 2);
    } else if (renderPhase === 'content') {
      // Phase 3: Render content elements
      setTimeout(() => {
        setAreComponentsReady(true);
        setTimeout(() => {
          setIsInitializing(false);
        }, RENDER_DELAY * 2);
      }, RENDER_DELAY);
    }
  }, [renderPhase]);
  
  // Handle theme toggle
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const newTheme = !prev;
      // Save to localStorage
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
    
    // Animate theme icon
    if (themeIconRef.current) {
      themeIconRef.current.style.transform = 'rotate(360deg)';
      setTimeout(() => {
        if (themeIconRef.current) {
          themeIconRef.current.style.transform = 'rotate(0deg)';
        }
      }, 300);
    }
    
    // Animate container background transition
    if (containerRef.current) {
      // Smooth background color transition
      containerRef.current.style.transition = 'background-color 0.5s ease';
      containerRef.current.style.backgroundColor = !isDarkMode 
        ? glitchThemes.dark.bgColor 
        : glitchThemes.light.bgColor;
    }
  }, [isDarkMode]);
  
  // Apply initial background color on mount
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.backgroundColor = currentTheme.bgColor;
    }
  }, [currentTheme.bgColor]);
  
  // Toggle reduced motion handler
  const toggleReducedMotion = useCallback(() => {
    setReducedMotion(prev => {
      const newValue = !prev;
      localStorage.setItem('reducedMotion', newValue.toString());
      return newValue;
    });
  }, []);
  
  // Dil değişikliğini handle etme
  const handleLanguageChange = async (langCode) => {
    // Aynı dili seçtiyse işlem yapma
    if (langCode === currentLanguage) return;
    
    setChangeError(null);
    setChangeSuccess(null);
    
    try {
      const success = await changeLanguage(langCode);
      
      if (success) {
        setChangeSuccess(t('languageChangedSuccess', 'Dil başarıyla değiştirildi.'));
        // Başarı mesajını 3 saniye sonra kaldır
        setTimeout(() => setChangeSuccess(null), 3000);
      } else {
        setChangeError(t('languageChangeError', 'Dil değiştirilirken bir hata oluştu.'));
      }
    } catch (error) {
      setChangeError(t('languageChangeError', 'Dil değiştirilirken bir hata oluştu.'));
      console.error('Dil değişim hatası:', error);
    }
  };
  
  // Apply all styles in one batch to prevent layout thrashing
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.id = 'languageAndRegionScreenStyles';
    styleElement.textContent = `
      @keyframes float {
        0% { transform: translateY(0px); }
        50% { transform: translateY(-10px); }
        100% { transform: translateY(0px); }
      }
      
      @keyframes pulse {
        0% { transform: scale(1); opacity: 1; }
        50% { transform: scale(1.05); opacity: 0.95; }
        100% { transform: scale(1); opacity: 1; }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      
      .letterglitch-container {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 0;
        contain: strict;
        overflow: hidden;
      }
      
      .content-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1;
        pointer-events: none;
        transition: background-color 0.5s ease;
      }
      
      .content-overlay-dark {
        background: rgba(0, 0, 0, 0.85);
      }
      
      .content-overlay-light {
        background: rgba(255, 255, 255, 0.75);
      }
      
      .theme-toggle {
        position: absolute;
        top: 20px;
        right: 20px;
        backdrop-filter: blur(8px);
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        z-index: 10;
        will-change: transform;
      }
      
      .theme-toggle-dark {
        background: rgba(255, 255, 255, 0.1);
      }
      
      .theme-toggle-light {
        background: rgba(30, 41, 59, 0.1);
      }
      
      .theme-toggle:hover {
        transform: rotate(15deg);
      }
      
      .theme-toggle-dark:hover {
        background: rgba(255, 255, 255, 0.2);
      }
      
      .theme-toggle-light:hover {
        background: rgba(30, 41, 59, 0.2);
      }
      
      .theme-icon {
        transition: transform 0.3s ease;
      }
      
      .glass-effect {
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border-radius: 20px;
        position: relative;
        z-index: 5;
        transition: all 0.5s ease;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
        transform: translateZ(0);
      }
      
      .glass-effect-dark {
        background: rgba(255, 255, 255, 0.15);
        border: 1px solid rgba(255, 255, 255, 0.3);
      }
      
      .glass-effect-light {
        background: rgba(255, 255, 255, 0.85);
        border: 1px solid rgba(30, 41, 59, 0.2);
      }
      
      .prevent-flicker {
        -webkit-transform: translate3d(0,0,0);
        -webkit-backface-visibility: hidden;
        transform: translateZ(0);
      }
      
      .settings-button {
        position: absolute;
        top: 20px;
        right: 70px;
        backdrop-filter: blur(8px);
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s ease;
        z-index: 10;
      }
      
      .settings-button-dark {
        background: rgba(255, 255, 255, 0.1);
        color: white;
      }
      
      .settings-button-light {
        background: rgba(30, 41, 59, 0.1);
        color: #1e293b;
      }
      
      .settings-button:hover {
        transform: rotate(30deg);
      }
      
      /* Toggle switch */
      .toggle-switch {
        width: 42px;
        height: 24px;
        border-radius: 12px;
        padding: 2px;
        cursor: pointer;
        position: relative;
        transition: background-color 0.3s;
      }
      
      .toggle-switch-on {
        background-color: #4f46e5;
      }
      
      .toggle-switch-off {
        background-color: #d1d5db;
      }
      
      .toggle-switch-knob {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: white;
        position: absolute;
        top: 2px;
        transition: left 0.3s;
      }
      
      .toggle-switch-on .toggle-switch-knob {
        left: 20px;
      }
      
      .toggle-switch-off .toggle-switch-knob {
        left: 2px;
      }
      
      /* Preferences panel */
      .preferences-panel {
        position: absolute;
        top: 70px;
        right: 20px;
        width: 280px;
        background-color: white;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        z-index: 20;
        overflow: hidden;
        transform-origin: top right;
        transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
      }
      
      .preferences-panel-dark {
        background-color: #1e293b;
        color: white;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      }
      
      /* Language selection card */
      .language-card {
        background-color: white;
        border-radius: 16px;
        overflow: hidden;
        transition: transform 0.2s, box-shadow 0.2s;
      }
      
      .language-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
      }
      
      .language-card-dark {
        background-color: rgba(30, 41, 59, 0.7);
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      
      /* Checkmark animation */
      @keyframes checkmarkAppear {
        0% { transform: scale(0); opacity: 0; }
        50% { transform: scale(1.2); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      
      .checkmark-selected {
        animation: checkmarkAppear 0.3s forwards;
      }
      
      /* Fixed background to prevent FOUC */
      .fixed-bg {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: -1;
      }
      
      /* Glowing flag effect */
      @keyframes flagGlow {
        0% { text-shadow: 0 0 5px rgba(255, 255, 255, 0.5); }
        50% { text-shadow: 0 0 15px rgba(255, 255, 255, 1), 0 0 20px rgba(99, 102, 241, 0.8); }
        100% { text-shadow: 0 0 5px rgba(255, 255, 255, 0.5); }
      }
      
      .flag-glow {
        animation: flagGlow 2s infinite;
      }
      
      /* Header with backdrop blur */
      .header-blur {
        backdrop-filter: blur(10px);
        -webkit-backdrop-filter: blur(10px);
      }
      
      .header-blur-dark {
        background-color: rgba(15, 23, 42, 0.8);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      }
      
      .header-blur-light {
        background-color: rgba(248, 250, 252, 0.8);
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      // Remove styles on unmount
      if (document.getElementById('languageAndRegionScreenStyles')) {
        document.getElementById('languageAndRegionScreenStyles').remove();
      }
    };
  }, []);
  
  // UserPreferencesPanel component
  const UserPreferencesPanel = () => {
    if (!showPreferences) return null;
    
    return (
      <div className={`preferences-panel ${isDarkMode ? 'preferences-panel-dark' : ''}`}>
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-lg">{t('settings.appSettings', 'Uygulama Ayarları')}</h3>
            <button 
              onClick={() => setShowPreferences(false)}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label={t('common.close', 'Kapat')}
            >
              <X size={18} />
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Theme toggle */}
            <div className="flex items-center justify-between">
              <span>{isDarkMode ? t('settings.darkMode', 'Karanlık Mod') : t('settings.lightMode', 'Aydınlık Mod')}</span>
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label={isDarkMode ? t('settings.switchToLightMode', 'Aydınlık Moda Geç') : t('settings.switchToDarkMode', 'Karanlık Moda Geç')}
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </div>
            
            {/* Reduced Motion toggle */}
            <div className="flex items-center justify-between">
              <span>{t('accessibility.reducedMotion', 'Azaltılmış Hareket')}</span>
              <div 
                className={`toggle-switch ${reducedMotion ? 'toggle-switch-on' : 'toggle-switch-off'}`}
                onClick={toggleReducedMotion}
                role="switch"
                aria-checked={reducedMotion}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && toggleReducedMotion()}
              >
                <div className="toggle-switch-knob"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Early-return a loading state if animations not ready yet
  if (isInitializing) {
    return (
      <div 
        style={{
          backgroundColor: currentTheme.bgColor,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <div 
      ref={containerRef}
      className="relative min-h-screen overflow-hidden"
      style={{
        backgroundColor: currentTheme.bgColor,
        willChange: 'background-color',
        transition: 'background-color 0.5s ease'
      }}
    >
      {/* LetterGlitch Background */}
      {renderPhase !== 'initial' && renderPhase !== 'background' && (
        <div className="letterglitch-container" aria-hidden="true">
          {areComponentsReady && (
            <LetterGlitch
              glitchColors={currentTheme.colors}
              glitchSpeed={currentTheme.glitchSpeed}
              centerVignette={currentTheme.centerVignette}
              outerVignette={false}
              smooth={true}
              charSet={currentTheme.charSet}
              density={reducedMotion ? 0.15 : 0.25}
              fadeInSpeed={1.5}
              opacity={0.6}
              skipWhenHidden={true}
              pauseWhenInactive={true}
              memoryOptimized={true}
            />
          )}
        </div>
      )}
      
      {/* Content overlay for better readability */}
      <div 
        className={`content-overlay ${isDarkMode ? 'content-overlay-dark' : 'content-overlay-light'}`}
        aria-hidden="true"
      ></div>
      
      {/* Theme Toggle Button */}
      <div 
        className={`theme-toggle ${isDarkMode ? 'theme-toggle-dark' : 'theme-toggle-light'} prevent-flicker`} 
        onClick={toggleTheme}
        aria-label={isDarkMode ? t('settings.switchToLightMode', 'Aydınlık Moda Geç') : t('settings.switchToDarkMode', 'Karanlık Moda Geç')}
        role="button"
        tabIndex={0}
      >
        <div ref={themeIconRef} className="theme-icon transition-smooth prevent-flicker">
          {isDarkMode ? 
            <Sun size={18} color="#f8fafc" className="prevent-flicker" /> : 
            <Moon size={18} color="#1e293b" className="prevent-flicker" />
          }
        </div>
      </div>
      
      {/* Settings Button */}
      <div
        className={`settings-button ${isDarkMode ? 'settings-button-dark' : 'settings-button-light'} prevent-flicker`}
        onClick={() => setShowPreferences(!showPreferences)}
        aria-label={t('settings.appSettings', 'Uygulama Ayarları')}
        role="button"
        tabIndex={0}
      >
        <Settings size={18} className="prevent-flicker" />
      </div>
      
      {/* User Preferences Panel */}
      <UserPreferencesPanel />
      
      {/* Main Layout */}
      <div className="flex flex-col min-h-screen relative z-10">
        {/* Header with backdrop blur */}
        <header className={`header-blur ${isDarkMode ? 'header-blur-dark' : 'header-blur-light'} p-4 shadow-lg sticky top-0 z-20`}>
          <div className="container mx-auto flex items-center space-x-4">
            <button 
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              onClick={goBack}
            >
              <ArrowLeft size={22} className={isDarkMode ? 'text-white' : 'text-gray-800'} />
            </button>
            <h1 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('languageAndRegion', 'Dil ve Bölge')}
            </h1>
          </div>
        </header>
        
        {/* Main Content */}
        <main className="flex-1 container mx-auto p-4 md:p-6 z-10">
          {/* Feedback Messages */}
          {changeSuccess && (
            <div className="glass-effect mb-6 py-3 px-4 border-l-4 border-green-500 flex items-center animate-fadeIn" style={{ backgroundColor: isDarkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)' }}>
              <Check size={20} className="text-green-500 mr-3 flex-shrink-0" />
              <span className={isDarkMode ? 'text-green-200' : 'text-green-800'}>
                {changeSuccess}
              </span>
            </div>
          )}
          
          {changeError && (
            <div className="glass-effect mb-6 py-3 px-4 border-l-4 border-red-500 flex items-center animate-fadeIn" style={{ backgroundColor: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)' }}>
              <AlertCircle size={20} className="text-red-500 mr-3 flex-shrink-0" />
              <span className={isDarkMode ? 'text-red-200' : 'text-red-800'}>
                {changeError}
              </span>
            </div>
          )}
          
          {/* Language Changing Indicator */}
          {isChangingLanguage && (
            <div className="glass-effect mb-6 py-3 px-4 flex items-center justify-center animate-fadeIn" style={{ backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)' }}>
              <div className="mr-3 w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <span className={isDarkMode ? 'text-blue-200' : 'text-blue-800'}>
                {t('changingLanguage', 'Dil değiştiriliyor...')}
              </span>
            </div>
          )}
          
          {/* Language Selection Section */}
          <div className={`glass-effect ${isDarkMode ? 'glass-effect-dark' : 'glass-effect-light'} mb-8 overflow-hidden`}>
            <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-start">
              <Globe size={22} className={`mr-3 flex-shrink-0 mt-1 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <div>
                <h2 className={`text-xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {t('languageSelection', 'Uygulama Dili')}
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {t('selectLanguageDescription', 'Uygulamanın arayüzünde kullanılacak dili seçin. Tüm menüler ve içerikler seçilen dilde görüntülenecektir.')}
                </p>
              </div>
            </div>
            
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              {languages.map(lang => (
                <div 
                  key={lang.code}
                  className={`language-card ${isDarkMode ? 'language-card-dark' : ''} cursor-pointer`}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <div className={`p-4 flex items-center justify-between ${currentLanguage === lang.code ? (isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-50') : ''}`}>
                    <div className="flex items-center">
                      <span className={`text-3xl mr-4 ${currentLanguage === lang.code ? 'flag-glow' : ''}`}>{lang.flag}</span>
                      <div>
                        <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{lang.name}</h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{lang.native || lang.code.toUpperCase()}</p>
                      </div>
                    </div>
                    
                    {currentLanguage === lang.code && (
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full ${isDarkMode ? 'bg-indigo-500' : 'bg-indigo-600'} checkmark-selected`}>
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Çeviriler Hakkında */}
          <div className={`glass-effect ${isDarkMode ? 'glass-effect-dark' : 'glass-effect-light'} mb-8`}>
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {t('aboutTranslations', 'Çeviriler Hakkında')}
              </h2>
            </div>
            
            <div className="p-5">
              <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('translationsDescription', 'Uygulamamız şu anda 5 dil desteklemektedir. Çeviriler sürekli olarak iyileştirilmektedir.')}
              </p>
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('translationFeedback', 'Eksik veya hatalı bir çeviri ile karşılaşırsanız, lütfen bize bildirin. Uygulamayı sizin için daha iyi hale getirmek istiyoruz.')}
              </p>
              
              <button className={`mt-4 px-4 py-2 rounded-lg ${isDarkMode ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-500 hover:bg-indigo-600'} text-white transition-colors font-medium text-sm`}>
                {t('reportTranslationIssue', 'Çeviri Hatası Bildir')}
              </button>
            </div>
          </div>
          
          {/* Bölge Ayarları */}
          <div className={`glass-effect ${isDarkMode ? 'glass-effect-dark' : 'glass-effect-light'}`}>
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {t('regionSettings', 'Bölge Ayarları')}
              </h2>
            </div>
            
            <div className="p-5">
              <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {t('regionSettingsDescription', 'Bölge ayarları, tarih ve saat biçimleri, para birimi gibi yerel tercihlerinizi etkiler.')}
              </p>
              
              <div className="space-y-6">
                {/* Tarih Formatı Kartı */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('dateFormat', 'Tarih Formatı')}</h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{t('dateFormatDescription', 'Tarihlerin nasıl gösterileceğini belirler')}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'} font-mono text-sm`}>
                      DD/MM/YYYY
                    </div>
                  </div>
                </div>
                
                {/* Saat Formatı Kartı */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('timeFormat', 'Saat Formatı')}</h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{t('timeFormatDescription', 'Saatlerin nasıl gösterileceğini belirler')}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'} font-mono text-sm`}>
                      24h
                    </div>
                  </div>
                </div>
                
                {/* Para Birimi Kartı */}
                <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{t('defaultCurrency', 'Varsayılan Para Birimi')}</h3>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{t('currencyDescription', 'Tutarların hangi para biriminde gösterileceğini belirler')}</p>
                    </div>
                    <div className={`px-3 py-1 rounded-lg ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700'} font-mono text-sm`}>
                      ₺ TRY
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default withTranslation(LanguageAndRegionScreen);